/**
 * 쿠팡 계정 줄의 동작 — 멀티계정관리 창(CoupangAccountModal)과 자동수집 창(AutoCollectModal)이 같이 쓴다.
 * 한 벌만 둔다: 로그인 확인·저장·상태등 판정이 두 곳에서 따로 놀면 한쪽 상태등만 틀린다.
 *
 * 확장과는 window.postMessage 로 주고받는다(script.js 가 중계). 쿠팡 아이디·비밀번호는 확장 저장소에만
 * 들어가고 쿠플러스 서버로 가지 않는다.
 */
import axios from 'axios';

export const CHANNEL_KEYS = [
    { key: 'wing', label: 'Wing' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'advertising', label: 'Ads' },
];
// 색만으로 알리지 않는다 — 점 안에 모양을 같이 넣는다
export const TONE_MARK = { ok: '✓', wait: '…', bad: '✕', none: '' };
// 쿠팡 로그인 확인 한 계정의 최대 대기(문자인증 3분 + 채널 확인 여유)
const LOGIN_TEST_TIMEOUT_MS = 6 * 60 * 1000;

// ── 확장 다리: 창이 둘이어도 듣는 곳은 하나 ──
// res → [{ fn, id }, ...]. 확장이 요청 id 를 돌려주면 그 요청의 대기자만 답을 받는다(A3 — 두 줄을 동시에 확인해도
// 두 번째의 「진행 중」 거절이 첫 번째 결과를 가로채지 않는다). 구 확장은 id 를 안 돌려준다 → 예전처럼 이름으로 전부 받는다.
const waiters = {};
let requestSeq = 0;
let activeRow = null;     // 지금 쿠팡 로그인 확인 중인 줄 — 확장 진행 알림을 이 줄에 붙인다
let listening = false;

function listen() {
    if (listening) return;
    listening = true;
    window.addEventListener('message', (event) => {
        const d = event.data || {};
        if (d.res === 'coupangLoginProgress') {
            if (activeRow) activeRow.progress = { ...activeRow.progress, [d.channel]: d.state };
            return;
        }
        // 자동수집이 끝난 뒤 맞춘 함께 전환 결과는 요청 번호 없이 따로 온다(헤더가 알림으로 띄운다) — 기다리는 요청의 답이 아니다
        if (d.res === 'coupangAccountSynced' && d.deferred) return;
        const list = waiters[d.res];
        if (!list || !list.length) return;
        if (d.requestId) {
            const mine = list.filter(w => w.id === d.requestId);
            if (!mine.length) return; // 다른 요청(이미 끝났거나 시간 초과된 것)의 답
            waiters[d.res] = list.filter(w => w.id !== d.requestId);
            mine.forEach(w => w.fn(d));
            return;
        }
        delete waiters[d.res];
        list.forEach(w => w.fn(d));
    });
}

/** 확장에 한 번 묻고 답을 기다린다. 확장이 없으면 timeout 뒤 null */
export function askExtension(greeting, payload, res, timeoutMs) {
    listen();
    requestSeq += 1;
    const requestId = `r${Date.now().toString(36)}-${requestSeq}`;
    // res 는 배열도 받는다 — 확장 판마다 답 이름이 다를 때 먼저 온 쪽을 쓴다
    const names = Array.isArray(res) ? res : [res];
    return new Promise((resolve) => {
        let done = false;
        const finish = (d) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            names.forEach((n) => { if (waiters[n]) waiters[n] = waiters[n].filter(w => w.fn !== finish); });
            resolve(d);
        };
        const timer = setTimeout(() => finish(null), timeoutMs);
        names.forEach((n) => (waiters[n] = waiters[n] || []).push({ fn: finish, id: requestId }));
        window.postMessage({ greeting, ...payload, requestId }, '*');
    });
}

/** 확장에 저장된 쿠팡 로그인 목록(비밀번호 제외). 확장이 없으면 null */
export async function loadCoupangAccounts() {
    const ext = await askExtension('getCoupangAccounts', {}, 'coupangAccounts', 5000);
    if (!ext) {
        // 운영 관측 — 확장이 있어도 멈춘 인스턴스는 대답을 안 한다(2026-09-27 재현 크롬·평소 크롬, 새로 올리면 정상).
        // 원인 미확정이라 얼마나 자주 나는지부터 본다. 비밀번호·아이디는 싣지 않는다
        axios.post('/api/diag/login-event', { flow: 'screen', step: 'getCoupangAccounts', verdict: 'no-response', client: 'screen' })
            .catch(e => console.error('[계정] 관측 이벤트 전송 실패', e));
    }
    return ext ? (ext.accounts || []) : null;
}

/** 쿠플러스 계정 하나(acc)에 맞는 확장의 쿠팡 로그인 — 멀티계정관리 창·자동수집 창이 같이 쓴다.
 *  서버가 판매자코드를 주면 그걸로, 옛 서버(판매자코드 없음)면 회사ID 가 하나로 맞을 때만 잇는다 */
export function findCoupangFor(coupang, acc) {
    if (acc.suppliercode) return coupang.find(c => c.suppliercode === acc.suppliercode) || null;
    if (!acc.companyid) return null;
    const same = coupang.filter(c => c.companyid === acc.companyid);
    return same.length === 1 ? same[0] : null;
}

let rowSeq = 0;
/** 한 줄의 상태. acc = 쿠플러스 계정 쪽, cp = 확장의 쿠팡 로그인 쪽 */
export function makeRow(acc, cp) {
    rowSeq += 1;
    return {
        key: acc.email || acc.suppliercode || 'row' + rowSeq,
        email: acc.email || '',
        name: acc.name || '',
        companyid: acc.companyid || (cp && cp.companyid) || '',
        suppliercode: acc.suppliercode || '',
        isOwner: !!acc.isOwner,
        isCurrent: !!acc.isCurrent,
        isNew: false,
        couplusEmail: '',
        couplusPw: '',
        cpId: (cp && cp.id) || '',
        cpSavedId: (cp && cp.id) || '', // 저장된 아이디 — 아이디만 바꾸고 비밀번호를 비웠는지 가른다(I5)
        cpPw: '',
        cpHasCred: !!(cp && cp.hasCredentials),
        // 브라우저 지금 계정인지(CLI current) — 옛 확장은 안 줘서 null(모름)
        cpCurrent: cp && typeof cp.current === 'boolean' ? cp.current : null,
        status: (cp && cp.status) || null,
        lastCheck: (cp && cp.lastCheck) || null,
        // 자동수집 — 새 확장은 계정마다 { next, last:{at,ok,fail,reason}, scheduleCount } 를 준다.
        // 서버(accountLink/list)는 lastCollect·collectAuth 를 준다. 없으면 null(구 확장·구 서버)
        autoCollect: (cp && cp.autoCollect) || null,
        lastCollect: acc.lastCollect || null,
        collectAuth: acc.collectAuth || null,
        showPw: false,
        busy: false,
        progress: {},
        result: null,
        // 확장이 비밀번호를 잠그지 못했거나(평문으로 남음) 잠근 것을 못 열면 줄에 바로 알린다(extpw-1)
        message: cp && cp.pwOpenFailed ? '저장된 쿠팡 비밀번호를 열지 못했습니다. 비밀번호를 다시 입력해 저장해 주세요.'
            : cp && cp.pwNotSealed ? '쿠팡 비밀번호가 잠기지 않은 채 저장돼 있습니다. 비밀번호를 다시 저장해 주세요.' : '',
        messageBad: !!(cp && (cp.pwOpenFailed || cp.pwNotSealed)),
    };
}

export function newRowKey() {
    rowSeq += 1;
    return 'new' + rowSeq;
}

/**
 * 이 줄을 확인할 수 있나.
 * 저장된 계정은 비밀번호 칸이 비어 있어도 된다 — 확장이 저장값으로 다시 확인한다(비밀번호는 화면으로 안 내려온다).
 */
export function isDirty(row) {
    if (row.isNew) return !!(row.couplusEmail || row.cpId);
    if (!row.suppliercode) return false;
    if (row.cpPw) return !!row.cpId;
    return !!row.cpHasCred;
}

/**
 * 목록을 새로 읽을 때 입력 중이던 값을 살린다 — 한 줄을 확인하고 다시 읽으면 다른 줄의 비밀번호가 지워지던 문제.
 * 결과(상태등·사유)도 살린다: 방금 확인한 줄의 결과가 새로 읽는 순간 사라지지 않게.
 */
export function carryOver(fresh, previous) {
    const byKey = {};
    (previous || []).forEach((r) => { byKey[r.key] = r; });
    fresh.forEach((r) => {
        const old = byKey[r.key];
        if (!old) return;
        if (old.cpPw) {
            r.cpPw = old.cpPw;
            r.cpId = old.cpId;
        }
        r.showPw = old.showPw;
        if (old.result) r.result = old.result;
        if (old.message) {
            r.message = old.message;
            r.messageBad = old.messageBad;
        }
    });
    return fresh;
}

/** 채널 상태등 { tone: ok|wait|bad|none, text } */
export function lightOf(row, key) {
    if (row.busy || row.result) {
        const done = row.result && row.result[key];
        if (done) {
            if (done.state === 'ok') return { tone: 'ok', text: '로그인됨' };
            if (done.state === 'noPermission') return { tone: 'none', text: '권한 없음' };
            return { tone: 'bad', text: done.reason || '실패' };
        }
        if (!row.busy) return { tone: 'none', text: '시도 안 함' };
        const p = row.progress[key];
        if (p === 'twoFactor') return { tone: 'wait', text: '문자인증 대기' };
        if (p === 'manualLogin') return { tone: 'wait', text: '쿠팡 창에서 직접 로그인' };
        if (key === 'wing' || p === 'pending') return { tone: 'wait', text: '진행 중' };
        return { tone: 'none', text: '대기' };
    }
    // 지금 세션(10분마다 확인) → 없으면 마지막 로그인 확인 결과
    // 10분 값이 마지막 로그인 확인(lastCheck.at)보다 옛것이면 저장 **전** 쿠키로 찍힌 값이다 — 쓰지 않는다
    const liveRaw = row.status && row.status.domains && row.status.domains[key];
    const lastAt = Number(row.lastCheck && row.lastCheck.at) || 0;
    const live = liveRaw && !(lastAt && Number(liveRaw.at) < lastAt) ? liveRaw : null;
    if (row.status && row.status.ownerMismatch && key === 'supplier') return { tone: 'bad', text: '다른 판매자 계정' };
    if (live && live.state === 'ok') return { tone: 'ok', text: '로그인됨' };
    if (live && live.state === 'logged-out') {
        // 브라우저 지금 계정이 아니면 몰래 로그인하지 않는다 — 그 계정으로 전환할 때 CLI 가 바로 다시 로그인한다(screen-command reloginPending)
        // 10-21 A01730650 윙 7일 풀림이 «로그인 풀림» 만 보여 방치됐다. 저장 아이디·비밀번호가 있고 지금 계정이 아닌 게 확실할 때만 약속한다
        if (row.cpCurrent === false && row.cpHasCred) return { tone: 'bad', text: '로그인 풀림 — 이 계정 쓸 때 자동으로 다시 로그인' };
        return { tone: 'bad', text: '로그인 풀림' };
    }
    const last = row.lastCheck && row.lastCheck[key];
    if (last && last.state === 'ok') return { tone: 'ok', text: '로그인됨' };
    if (last && last.state === 'noPermission') return { tone: 'none', text: '권한 없음' };
    if (last) return { tone: 'bad', text: last.reason || '실패' };
    return { tone: 'none', text: row.cpHasCred ? '확인 전' : '미등록' };
}

// 자동수집 실패 사유 → 화면 문구(확장·서버가 주는 값은 개발용 코드다)
const AUTO_COLLECT_REASON_TEXT = {
    'no-permission': '권한 없음',
    'no-plan-permission': '이용 권한 없음',
    'login-failed': '쿠팡 로그인 실패',
    'no-credentials': '쿠팡 비밀번호 미저장',
    'cli-off': '연결 프로그램이 꺼져 있음',
    'cli-outdated': '연결 프로그램 업데이트 필요',
    'skipped-busy': '사용 중이라 건너뜀',
    'login-blocked': '쿠팡 로그인 잠시 제한',
    'interrupted': '중단됨',
    'interrupted-by-user': '다른 계정으로 바뀌어 중단됨',
    'user-changed-account': '다른 계정으로 바뀌어 중단됨',
    'fence-broken': '다른 계정으로 바뀌어 중단됨',
    'archive-failed': '계정 전환 실패',
    'verify-failed': '계정 확인 실패',
};

function fmtWhen(v) {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    const p = n => String(n).padStart(2, '0');
    return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 일정 개수 — 새 확장만 준다. 모르면 null */
export function autoCollectCount(row) {
    const ac = row && row.autoCollect;
    return ac && typeof ac.scheduleCount === 'number' ? ac.scheduleCount : null;
}

/**
 * 계정 줄 아래 한 줄 「마지막 수집 … · 다음 …」. 보여줄 게 없으면 빈 문자열.
 * 확장 값(autoCollect.last)이 먼저, 없으면 서버 값(lastCollect)
 */
export function autoCollectSummary(row) {
    if (!row) return '';
    const ac = row.autoCollect || {};
    const last = ac.last || row.lastCollect || null;
    const parts = [];
    if (last && last.at) {
        let t = `마지막 수집 ${fmtWhen(last.at)}`;
        const ok = Number(last.ok) || 0;
        const fail = Number(last.fail) || 0;
        if (ok || fail) t += ` (성공 ${ok}${fail ? ` · 실패 ${fail}` : ''})`;
        if (fail && last.reason) {
            // 모르는 코드는 그대로 보이지 않는다 — 한글 문구만 그대로 쓴다
            const known = AUTO_COLLECT_REASON_TEXT[last.reason];
            const reason = known || (/[가-힣]/.test(last.reason) ? last.reason : '');
            if (reason) t += ` ${reason}`;
        }
        parts.push(t);
    }
    if (ac.next) {
        const n = fmtWhen(ac.next);
        if (n) parts.push(`다음 ${n}`);
    }
    return parts.join(' · ');
}

function setRowMessage(row, text, bad) {
    row.message = text;
    row.messageBad = !!bad;
}

/** 새 줄의 쿠플러스 계정을 잇는다(기존 「계정 추가」 API) */
async function linkCouplus(row) {
    let res;
    try {
        res = await axios.post('/api/accountLink/add', { email: row.couplusEmail, pw: row.couplusPw });
    } catch (e) {
        console.error('[계정] 쿠플러스 계정 추가 실패', e);
        setRowMessage(row, '쿠플러스 계정을 추가하지 못했습니다.', true);
        return false;
    }
    if (!res.data || res.data.res !== 'success') {
        setRowMessage(row, (res.data && res.data.alert) || '쿠플러스 계정을 추가하지 못했습니다.', true);
        return false;
    }
    row.couplusPw = '';
    // 방금 이은 계정의 회사ID·판매자코드를 받는다
    try {
        const list = await axios.post('/api/accountLink/list', {});
        const acc = ((list.data && list.data.accounts) || []).find(a => a.email === row.couplusEmail);
        if (acc) {
            row.email = acc.email;
            row.companyid = acc.companyid || '';
            row.suppliercode = acc.suppliercode || '';
        }
    } catch (e) {
        console.error('[계정] 추가한 계정 정보 조회 실패', e);
    }
    return true;
}

/**
 * 계정 전환 확인창을 만든다 — this.$bvModal 은 컴포넌트에만 있어서 컴포넌트가 만들어 saveRow 에 넘긴다.
 * 다크 테마는 앱 공통 규칙(body.dark .modal-content, _dark.scss)이 칠한다.
 * @param {object} vm 컴포넌트(this)
 * @param {(code:string)=>string} [labelOf] 판매자코드 → 화면에 보일 계정 이름
 * @returns {({row, openTabs, blankTabs, fromAccount}) => Promise<boolean>}
 */
export function makeSwitchConfirm(vm, labelOf) {
    const label = code => (code && labelOf && labelOf(code)) || code || '';
    return async ({ row, openTabs, blankTabs, fromAccount }) => {
        const from = label(fromAccount);
        const to = row.companyid || row.suppliercode;
        // 대상 계정이 권한 없는 채널(예: 판매자센터) 창은 새로고침하면 새 계정 로그인까지 풀린다 — 확장이 빈 화면으로 바꾼다
        const blank = blankTabs > 0
            ? ` 그중 ${to} 계정이 쿠팡 판매자센터를 쓰지 않아 채널 창 ${blankTabs}개는 새로고침하지 않고 빈 화면으로 바꿉니다.`
            : '';
        const tabs = openTabs > 0 ? ` 열려 있는 쿠팡 창 ${openTabs}개는 새로고침됩니다(입력 중인 내용은 사라집니다).${blank}` : '';
        // 안 A: 로그인 확인은 **항상** 묻는다. 다른 계정(주인 모르는 로그인 포함)이면 그 로그인 상태를 먼저 보관한다고 알린다
        const other = !!fromAccount && fromAccount !== row.suppliercode;
        const unknownOwner = !fromAccount;
        const msg = other
            ? `지금 브라우저에는 다른 쿠팡 계정(${from})이 로그인돼 있습니다. ${to} 계정으로 로그인을 확인하면 그 로그인 상태를 먼저 보관한 뒤 ${to} 계정으로 바뀝니다(보관하지 못하면 아무것도 바꾸지 않고 멈춥니다).${tabs} 계속할까요?`
            : `${to} 계정으로 쿠팡 로그인을 확인합니다. ${unknownOwner ? '브라우저에 로그인된 쿠팡 계정이 있으면 그 로그인 상태를 먼저 보관한 뒤 바뀝니다.' : '이 계정이 이미 로그인돼 있어도 새로 로그인합니다.'}${tabs} 계속할까요?`;
        try {
            return !!(await vm.$bvModal.msgBoxConfirm(msg, { title: '쿠팡 로그인 확인', okTitle: '계속', cancelTitle: '취소', centered: true }));
        } catch (e) {
            console.error('[계정] 로그인 확인 경고창 실패', e);
            return false;
        }
    };
}

/** 확장에 로그인 확인을 요청한다. confirmSwitch = 사람이 전환 확인창에서 「계속」을 눌렀다 */
function askLoginTest(row, useSaved, supportsConfirm, confirmSwitch) {
    const ask = useSaved
        ? { useSaved: true, supplierCode: row.suppliercode }
        : { id: row.cpId, pw: row.cpPw, supplierCode: row.suppliercode };
    // supportsConfirm: 이 화면은 확인창을 띄울 수 있다 — 확장은 이 표식이 있을 때만 needConfirm 을 돌려준다(구 화면 호환)
    if (supportsConfirm) Object.assign(ask, { supportsConfirm: true, confirmSwitch: !!confirmSwitch });
    return askExtension('coupangLoginTest', ask, 'coupangLoginTest', LOGIN_TEST_TIMEOUT_MS);
}

/** 쿠팡 로그인 확인(세 채널) → 확장에 저장 */
async function checkCoupang(row, confirmSwitch, preConfirmed) {
    activeRow = row;
    // 비밀번호를 새로 안 넣었으면 저장값으로 재확인한다 — 확장이 자기 저장소에서 채운다
    const useSaved = !row.cpPw && row.cpHasCred;
    // 저장 경고창에서 이미 「진행」을 눌렀으면 확장에 곧바로 계속(confirmSwitch)으로 보낸다 — 두 번째 확인창 없음
    let d = await askLoginTest(row, useSaved, preConfirmed || !!confirmSwitch, !!preConfirmed);
    if (d && d.needConfirm) {
        // 다른 계정 쿠팡 창이 열려 있다 — 확장은 아무것도 바꾸지 않고 돌아왔다. 「취소」면 상태 그대로, 안내 없이 끝낸다
        activeRow = null;
        // 확인창이 떠 있는 동안은 요청 전이다 — 진행 중(스피너·상태등 대기)으로 보이면 안 된다. 취소면 직전 결과를 그대로 돌려놓는다
        const prevResult = row.__prevResult;
        const wasBusy = row.busy;
        row.busy = false;
        row.result = prevResult === undefined ? null : prevResult;
        const go = await confirmSwitch({ row, openTabs: d.openTabs, blankTabs: d.blankTabs || 0, fromAccount: d.fromAccount });
        row.busy = wasBusy;
        if (!go) return;
        row.result = null;
        activeRow = row;
        d = await askLoginTest(row, useSaved, true, true);
    }
    activeRow = null;
    if (!d) {
        row.result = { wing: { state: 'fail', reason: '응답이 없습니다' } };
        setRowMessage(row, '쿠팡 로그인 확인에 응답이 없습니다.', true);
        // 운영 관측 — 확장이 끝내 답하지 않았다(구 확장·워커 사망 등). 비밀번호·아이디는 싣지 않는다
        axios.post('/api/diag/login-event', { flow: 'screen', step: 'askLoginTest', verdict: 'no-response', client: 'screen' })
            .catch(e => console.error('[계정] 관측 이벤트 전송 실패', e));
        return;
    }
    if (!d.result) {
        const reason = d.msg || '로그인에 실패했습니다.';
        // 로그인 관문 거절(throttled) = 확인을 아예 안 했다(쿠팡 요청 0). 채널을 ✕ 로 바꾸지 않고
        // 직전 상태(지금 세션·마지막 확인)를 그대로 둔다 — 안내 문구만 붙인다.
        // 실패여도 확장이 확인한 채널 결과를 보냈으면 그대로 쓴다(예: 윙 ok + 공통 로그인 없음 — Wing 을 ✕ 로 칠하지 않는다).
        // 채널 결과가 없을 때만 예전처럼 Wing 실패로 본다
        const hasChannels = d.channels && typeof d.channels === 'object' && Object.keys(d.channels).length > 0;
        row.result = (d.throttled || d.archiveFailed) ? null : (hasChannels ? d.channels : { wing: { state: 'fail', reason } });
        setRowMessage(row, reason, true);
        return;
    }
    const channels = d.channels || { wing: { state: 'ok', reason: '' } };
    row.result = channels;
    // 저장값으로 재확인했으면 id·pw 를 비워 보낸다 — 확장이 저장된 값을 그대로 둔다(채널 결과만 기록)
    const saved = await askExtension('saveAutoCollectCredentials', {
        credentials: {
            id: useSaved ? '' : row.cpId,
            pw: useSaved ? '' : row.cpPw,
            companyid: row.companyid,
            suppliercode: row.suppliercode,
            channelResults: channels,
        },
    }, 'autoCollectCredentialsSaved', 10000);
    if (!saved || !saved.success) {
        setRowMessage(row, '로그인은 확인했지만 저장하지 못했습니다.', true);
        return;
    }
    row.cpPw = '';
    row.cpHasCred = true;
    const failed = Object.keys(channels)
        .filter(k => !['ok', 'noPermission'].includes(channels[k].state)) // 권한 없음은 다시 시도할 대상이 아니다
        .map(k => (CHANNEL_KEYS.find(c => c.key === k) || { label: k }).label);
    // 로그인은 됐지만 사람이 알아야 할 것(예: 판매자센터 비밀번호 만료 예정) — 확장이 채널 결과에 notice 로 붙인다
    const notices = Object.keys(channels)
        .filter(k => channels[k] && channels[k].notice)
        .map(k => `${(CHANNEL_KEYS.find(c => c.key === k) || { label: k }).label}: ${channels[k].notice}`);
    const base = failed.length ? `저장함 · 다음 자동 로그인 때 다시 시도: ${failed.join('·')}` : '저장함';
    setRowMessage(row, notices.length ? `${base} · ${notices.join(' · ')}` : base, false);
}

/** 줄 입력 검사 — 쿠팡 칸이 있으면 아이디·비밀번호가 짝이 맞아야 한다. 문제면 줄에 안내를 쓰고 false */
function validateRow(row) {
    const hasCoupang = !!(row.cpId || row.cpPw || row.cpHasCred);
    if (hasCoupang && (row.cpPw ? !row.cpId : !row.cpHasCred)) {
        setRowMessage(row, '쿠팡 아이디와 비밀번호를 모두 입력하세요.', true);
        return false;
    }
    // 아이디만 바꾸고 비밀번호를 비웠다 — 저장값으로 재확인하면 바꾼 아이디가 조용히 버려진다(I5)
    if (hasCoupang && !row.cpPw && row.cpHasCred && row.cpId && row.cpSavedId && row.cpId !== row.cpSavedId) {
        setRowMessage(row, '아이디를 바꿨으면 비밀번호도 입력하세요.', true);
        return false;
    }
    return true;
}

/**
 * 그 줄 저장 (안 A, 2026-10-15): (새 줄이면) 쿠플러스 계정 잇기 → 쿠팡 아이디·비밀번호만 저장.
 * **쿠키 삭제·탭 열기·로그인 제출 0** — 쿠팡 로그인 확인은 별도 「로그인 확인」 버튼(checkRowLogin)이 한다.
 * @param {object} [opts] (호환용 — confirmSave·confirmSwitch 는 쓰지 않는다)
 */
export async function saveRow(row, opts = {}) { // eslint-disable-line no-unused-vars
    try {
        if (row.isNew && (!row.couplusEmail || !row.couplusPw)) {
            setRowMessage(row, '쿠플러스 아이디와 비밀번호를 입력하세요.', true);
            return;
        }
        if (!validateRow(row)) return;
        row.busy = true;
        row.result = null;
        row.progress = {};
        setRowMessage(row, '', false);
        if (row.isNew && !(await linkCouplus(row))) return;
        if (!(row.cpId && row.cpPw)) {   // 새로 넣은 쿠팡 아이디·비밀번호가 없으면 저장할 쿠팡 정보가 없다
            setRowMessage(row, row.isNew ? '쿠플러스 계정을 추가했습니다.' : '바뀐 쿠팡 정보가 없습니다.', false);
            return;
        }
        if (!row.suppliercode) {
            setRowMessage(row, '판매자코드가 없는 계정이라 쿠팡 정보를 저장할 수 없습니다.', true);
            return;
        }
        const kept = await askExtension('saveAutoCollectCredentials', {
            credentials: { id: row.cpId, pw: row.cpPw, companyid: row.companyid, suppliercode: row.suppliercode },   // channelResults 없음 — 로그인 결과·채널 상태는 건드리지 않는다
        }, 'autoCollectCredentialsSaved', 10000);
        if (!kept || !kept.success) { setRowMessage(row, '아이디·비밀번호를 저장하지 못했습니다.', true); return; }
        row.cpPw = '';
        row.cpHasCred = true;
        row.cpSavedId = row.cpId;
        setRowMessage(row, '저장함 · 쿠팡 로그인은 «로그인 확인»을 눌러 확인하세요', false);
    } catch (e) {
        console.error('[계정] 저장 실패', e);
        setRowMessage(row, '저장하지 못했습니다. 다시 시도해 주세요.', true);
    } finally {
        activeRow = null;
        row.busy = false;
    }
}

/**
 * 그 줄 쿠팡 로그인 확인 (안 A): 확장이 **항상** 먼저 needConfirm 으로 돌려주고(부작용 0), 화면이 확인창을 띄운 뒤 「계속」이면
 * 확장이 (다른 계정이 로그인돼 있으면 보관한 뒤) 이 계정으로 로그인 확인. 「취소」면 거기서 끝.
 * @param {object} opts
 * @param {Function} opts.confirmSwitch makeSwitchConfirm 결과 — 필수(없으면 요청 자체를 보내지 않는다)
 */
export async function checkRowLogin(row, opts = {}) {
    try {
        if (typeof opts.confirmSwitch !== 'function') return;   // 확인창을 못 띄우면 진행하지 않는다
        if (!validateRow(row)) return;
        if (!row.suppliercode) {
            setRowMessage(row, '판매자코드가 없는 계정이라 쿠팡 로그인을 확인할 수 없습니다.', true);
            return;
        }
        if (!(row.cpPw ? row.cpId : row.cpHasCred)) {
            setRowMessage(row, '쿠팡 아이디와 비밀번호를 먼저 입력해 저장하세요.', true);
            return;
        }
        row.__prevResult = row.result;   // 확인창 «취소» 때 되돌릴 직전 결과
        row.busy = true;
        row.progress = {};
        setRowMessage(row, '', false);
        await checkCoupang(row, opts.confirmSwitch, false);
    } catch (e) {
        console.error('[계정] 로그인 확인 실패', e);
        setRowMessage(row, '로그인 확인에 실패했습니다. 다시 시도해 주세요.', true);
    } finally {
        activeRow = null;
        row.busy = false;
    }
}

// ── 쿠팡 쇼핑(www.coupang.com) 공통 계정 — 전체에서 1개. 상품검색·리뷰 등에 쓴다 ──
// 비밀번호는 확장이 받아 로컬 연결 프로그램에 바로 넘긴다(확장·서버에 저장하지 않는다).

/** 상태 조회. 확장이 없으면 { ok:false, error } */
export async function loadShoppingAccount() {
    // 연결 프로그램이 쇼핑 로그인을 하는 동안은 상태 응답이 7초 넘게 걸린다(2026-09-27 실측 7.1초).
    // 5초로 자르면 로그인이 잘 되는 중에도 「응답이 없습니다」로 끝나고 상태 확인도 멈춘다
    const d = await askExtension('getShopAccount', {}, 'shopAccount', 15000);
    return d || { ok: false, error: '쿠플러스 확장에서 응답이 없습니다. 페이지를 새로고침해 보세요.' };
}

export async function saveShoppingAccount(id, pw) {
    const d = await askExtension('saveShopAccount', { id, pw }, 'shopAccountSaved', 15000);
    return d || { ok: false, error: '쿠플러스 확장에서 응답이 없습니다. 페이지를 새로고침해 보세요.' };
}

export async function removeShoppingAccount() {
    const d = await askExtension('removeShopAccount', {}, 'shopAccountRemoved', 10000);
    return d || { ok: false, error: '쿠플러스 확장에서 응답이 없습니다. 페이지를 새로고침해 보세요.' };
}

// ── 쿠팡 Open API 키 (계정 줄마다 · 판매자코드 기준) ──
// 키는 확장 → 이 PC 의 couplus CLI 에 암호화해 둔다(couplus-cli/core/openapi). 서버로 가지 않는다. 키 값은 다시 보여 주지 않는다.
const NO_EXT = { ok: false, error: '쿠플러스 확장에서 응답이 없습니다. 페이지를 새로고침해 보세요.' };

/** 이 PC 에 키가 있는 판매자코드 목록 → { [vendorId]: 상태 }. 확장·CLI 가 없으면 null */
export async function loadOpenApiKeys() {
    const d = await askExtension('getOpenApiKeys', {}, 'openApiKeys', 8000);
    if (!d || !d.ok || !Array.isArray(d.accounts)) return null;
    const map = {};
    d.accounts.forEach((a) => { if (a && a.vendorId) map[a.vendorId] = a; });
    return map;
}

export async function saveOpenApiKey(vendorId, accessKey, secretKey) {
    return (await askExtension('saveOpenApiKey', { vendorId, accessKey, secretKey }, 'openApiKeySaved', 10000)) || NO_EXT;
}

export async function removeOpenApiKey(vendorId) {
    return (await askExtension('removeOpenApiKey', { vendorId }, 'openApiKeyRemoved', 10000)) || NO_EXT;
}

/** 키 확인 — 쿠팡에 조회 한 번(쓰기 0). 결과에 지금 PC 공인 IP 가 같이 온다 */
export async function checkOpenApiKey(vendorId) {
    return (await askExtension('checkOpenApiKey', { vendorId }, 'openApiKeyChecked', 40000)) || NO_EXT;
}

/** 판매자배송 매출 인식 내역(조회만) — CLI 가 끝 쪽까지 받아 옵션 줄로 편다 → { ok, rows } · 키 없으면 { ok:false, needKey:true } */
export async function fetchOpenApiRevenue(vendorId, from, to) {
    return (await askExtension('fetchOpenApiRevenue', { vendorId, from, to }, 'openApiRevenue', 180000)) || NO_EXT;
}

export async function loadPublicIp(fresh) {
    return (await askExtension('getPublicIp', { fresh: !!fresh }, 'publicIp', 8000)) || NO_EXT;
}

/** 표 줄 표시 { tone, text } — 설정됨/안 됨 */
export function openApiStatusOf(st) {
    if (!st || !st.registered) return { tone: 'none', text: '안 됨' };
    if (st.lastCheck && st.lastCheck.ok === false) return { tone: 'bad', text: '설정됨 · 확인 실패' };
    return { tone: 'ok', text: '설정됨' };
}

// ── 1688 로그인 계정 — 이 PC 공통 하나(판매자 계정별 아님). 판매자 협상 도구가 1688 에 로그인할 때 쓴다 ──
// 비밀번호는 쿠팡 계정처럼 확장에 봉해 둔다(연결 프로그램·서버로 보내지 않는다). 저장하면 확장이 크롬 1688 탭에서 로그인한다.

/** { ok, hasCredentials, id(가림) } | { ok:false, error } */
export async function get1688Account() {
    const d = await askExtension('get1688Account', {}, 'alibaba1688Account', 10000);
    return d || { ok: false, error: '쿠플러스 확장에서 응답이 없습니다. 페이지를 새로고침해 보세요.' };
}

export async function save1688Account(id, pw) {
    const d = await askExtension('save1688Account', { id, pw }, 'alibaba1688AccountSaved', 10000);
    return d || { ok: false, error: '쿠플러스 확장에서 응답이 없습니다. 페이지를 새로고침해 보세요.' };
}

/**
 * «로그인 확인» — 확장에 봉해 둔 1688 계정으로 로그인 1번(쿠팡 계정 줄의 로그인 확인과 같은 꼴). 아이디·비밀번호는 싣지 않는다.
 * { ok } | { ok:false, error }
 */
export async function login1688Account() {
    const d = await askExtension('login1688Account', {}, 'alibaba1688AccountLogin', 10000);
    return d || { ok: false, error: '쿠플러스 확장에서 응답이 없습니다. 페이지를 새로고침해 보세요.' };
}

// 지우기는 확장 저장을 지우고 연결 프로그램의 1688 쿠키 통·협상 크롬 프로필까지 지운 뒤 답한다 — 10초를 넘길 수 있다.
// 그 사이 확장은 CLI 답을 계속 기다리므로(중계에 시간 제한 없음) 화면만 「응답 없음」 대신 「지우는 중」으로 둔다
export const ALI_REMOVE_PENDING_MSG = '지우는 중 — 잠시 뒤 다시 읽어 주세요';
/** { ok } | { ok:false, error } | { ok:false, pending:true, error } (10초 안에 답이 없을 때 — 실패가 아니다) */
export async function remove1688Account() {
    const d = await askExtension('remove1688Account', {}, 'alibaba1688AccountRemoved', 10000);
    return d || { ok: false, pending: true, error: ALI_REMOVE_PENDING_MSG };
}

/**
 * 1688 계정 상태 표시 { tone: ok|wait|bad|none, text } — CLI core/nego1688/session.js 의 state 를 그대로 옮긴다.
 * needs-captcha 는 사람이 그 크롬 창에서 풀어야 한다(1688 탭은 확장이 앞으로 띄운다).
 */
export function ali1688StatusOf(st) {
    if (!st) return { tone: 'none', text: '확인 중' };
    if (!st.ok) return { tone: 'bad', text: st.error || '상태를 확인하지 못했습니다' };
    if (!st.hasCredentials) return { tone: 'none', text: '미설정' };
    if (st.unreadable) return { tone: 'bad', text: '저장됨 · 다시 입력해 주세요' };
    const who = st.id ? ` (${st.id})` : '';
    if (st.state === 'needs-captcha') return { tone: 'wait', text: '캡차 풀어 주세요 — 앞에 띄운 크롬 창에서' };
    if (st.state === 'logging-in') return { tone: 'wait', text: '로그인 중' };
    if (st.state === 'logged-in') return { tone: 'ok', text: `로그인됨${who}` };
    if (st.state === 'failed') return { tone: 'bad', text: '실패' };
    // 옛 CLI(state 없음)·데몬 재시작 뒤 — 저장만 된 상태
    return { tone: 'wait', text: `저장됨${who} · 로그인 전` };
}

/** 1688 로그인이 아직 도는 중인가(화면이 다시 읽을지) */
export function ali1688Pending(st) {
    return !!(st && st.ok && st.hasCredentials && (st.state === 'logging-in' || st.state === 'needs-captcha'));
}

/** 상태 표시 { tone: ok|wait|bad|none, text } */
export function shoppingStatusOf(st) {
    if (!st) return { tone: 'none', text: '확인 중' };
    if (!st.ok) return { tone: 'bad', text: st.error || '상태를 확인하지 못했습니다' };
    if (st.loggingIn) return { tone: 'wait', text: '로그인 중' };
    if (st.session && st.session.ready) return { tone: 'ok', text: '로그인됨' };
    if (st.lastError) return { tone: 'bad', text: `실패 · ${st.lastError}` };
    if (!st.hasCredentials) return { tone: 'none', text: '미설정' };
    return { tone: 'wait', text: '로그인 대기' };
}

// ── 쿠플러스·쿠팡 계정 함께 전환 ──
// 쿠플러스 계정 1개 = 쿠팡 판매자코드 1개. 한쪽을 바꾸면 다른 쪽도 맞춰 두 계정이 어긋나지 않게 한다.
// 스위치는 확장이 브라우저별로 저장한다(기본 켜짐).

/** 스위치 상태. 확장이 없거나 옛 확장이면 null → 화면은 토글을 숨기고 예전처럼 동작한다 */
export async function loadAccountSync() {
    const d = await askExtension('getAccountSync', {}, 'accountSync', 2000);
    return d && typeof d.enabled === 'boolean' ? d.enabled : null;
}

export async function saveAccountSync(enabled) {
    // 확장 판에 따라 'accountSyncSaved' {ok, enabled} 또는 'accountSync' {enabled} 로 답한다
    const d = await askExtension('setAccountSync', { enabled: !!enabled }, ['accountSyncSaved', 'accountSync'], 3000);
    return !!(d && d.ok !== false && d.enabled === !!enabled);
}

// 계정을 바꾸는 동안(보관 → 주입 → 확인, 안 되면 저장 비밀번호로 로그인) 기다리는 시간.
// 비밀번호 경로는 문자인증 대기까지 최대 150초 — 그보다 길게 본다
const ACCOUNT_SYNC_TIMEOUT_MS = 170000;

/**
 * 쿠플러스 계정을 바꾼 뒤 브라우저 쿠팡 계정도 그 계정으로 맞춘다.
 * 다른 계정 쿠팡 창이 열려 있으면 확장은 아무것도 바꾸지 않고 needConfirm 으로 돌아온다 → 한 번 묻는다.
 * @param {{suppliercode, companyid, email}} target
 * @param {Function} confirmSwitch makeSwitchConfirm 결과
 * @returns {Promise<{text:string, bad:boolean}|null>} 화면 알림 한 줄. 알릴 것 없으면 null
 */
export async function syncCoupangAccount(target, confirmSwitch) {
    const ask = (confirmed) => askExtension('syncCoupangAccount', {
        suppliercode: target.suppliercode,
        companyid: target.companyid,
        email: target.email,
        source: 'couplus-switch',
        supportsConfirm: true,
        confirmSwitch: !!confirmed,
    }, 'coupangAccountSynced', ACCOUNT_SYNC_TIMEOUT_MS);
    let d = await ask(false);
    if (d && d.needConfirm) {
        const row = { companyid: target.companyid, suppliercode: target.suppliercode };
        if (!(await confirmSwitch({ row, openTabs: d.openTabs, blankTabs: d.blankTabs || 0, fromAccount: d.fromAccount }))) {
            return { text: '쿠팡 계정은 그대로입니다(전환 취소).', bad: false };
        }
        d = await ask(true);
    }
    return accountSyncNotice(d, target);
}

/** 확장 응답 → 화면 알림 한 줄 */
export function accountSyncNotice(d, target) {
    const who = (target && (target.companyid || target.suppliercode)) || '';
    if (!d) return { text: '쿠팡 계정을 맞추지 못했습니다(응답 없음). 멀티계정관리에서 확인해 주세요.', bad: true };
    if (d.ok) {
        if (d.via === 'already') return null;
        return { text: `쿠팡 계정도 ${who}(으)로 바꿨습니다.`, bad: false };
    }
    if (d.disabled || d.skipped === 'disabled') return null;   // 스위치 꺼짐
    if (d.skipped === 'no-credentials') return { text: '쿠팡 계정은 그대로입니다(로그인 정보 미저장).', bad: false };
    if (d.busy) {
        const eta = d.etaMin ? `약 ${d.etaMin}분 뒤 ` : '';
        const reason = d.busyReason || (typeof d.busy === 'string' ? d.busy : '');
        const what = { autoCollect: '자동수집이', agent: '다른 작업이', 'login-flow': '쿠팡 로그인이', 'account-sync': '계정 전환이' }[reason] || '다른 작업이';
        return { text: `${what} 진행 중이라 쿠팡 계정은 끝난 뒤 ${eta}자동으로 맞춥니다.`, bad: false };
    }
    const why = d.error || d.msg;
    return { text: `쿠팡 계정을 바꾸지 못했습니다${why ? `: ${why}` : '.'}`, bad: true };
}

export { setRowMessage };
